import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { FormGroup } from '@angular/forms';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { ReplaySubject, debounceTime, distinctUntilChanged, tap, takeUntil } from 'rxjs';
import { utils } from '../../../helper/utils';
import { ApprovalFilterForm } from '../../../form-model/work-order-filter.form.model';
import { WorkOrderSearchTextForm } from '../../../form-model/work-order-search-text.form.model';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { BulkWorkOrderCompletionBody } from '../../../models/bulk-work-order/bulk-work-order-completion.model';
import { manualApprovalFilter } from '../../../models/manual-approval-filter.model';
import { ManualApprovalHeader } from '../../../models/manual-approval-header.model';
import { ApprovalWoService } from '../../../services/approval-wo.service';

@Component({
  selector: 'app-approval-all-wo-grid',
  imports: [NgxSkeletonLoaderModule, RouterModule, LoaderComponent],
  templateUrl: './approval-all-wo-grid.component.html',
  styleUrl: './approval-all-wo-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ApprovalAllWoGridComponent {
  selected = [''];
  selectedRows: BulkWorkOrderCompletionBody[] = [];
  data: ManualApprovalHeader[] = [];
  mockDataForLoadingAnimation: { id: number }[] = [...Array(20).keys()].map((i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  filterParam: manualApprovalFilter = {};
  getWorkOrdersApiCallStatus: APICallStatus = 'NOT_STARTED';
  hasData: boolean = false;
  urlNavigate: string | undefined;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    workOrderFilterForm: FormGroup<ApprovalFilterForm>;
    workOrderSearchTextForm: FormGroup<WorkOrderSearchTextForm>;
  }>;
  @Output() selectedRowsChange = new EventEmitter<BulkWorkOrderCompletionBody[]>(); // Emit the selected rows
  columns: Array<TableColumn> = [
    {
      id: 'priorityFlag',
      label: '',
      sortDisabled: true,
    },
    {
      id: 'maintenanceOrderNumber',
      label: 'Work Order Number',
      sortDisabled: true,
    },
    {
      id: 'equipmentNumber',
      label: 'Equipment Number',
      sortDisabled: true,
    },
    {
      id: 'totalOrderCostUSD',
      label: 'Total Value(USD)',
      dataType: {
        type: 'number',
        formatter: (value, row) => {
          return utils.numberFormatter.format(Number(value));
        },
      },
    },
    {
      id: 'totalOrderCost',
      label: 'Total Value(Shop Currency)',
      sortDisabled: true,
      dataType: {
        type: 'number',
        formatter: (value, row) => {
          return `${(row.original as ManualApprovalHeader).isoCurrencyCode} ${utils.numberFormatter.format(Number(value))}`;
        },
      },
    },
    {
      id: 'maintenanceRepairModeCode',
      label: 'Mode',
    },
    {
      id:'holdForOtherMode',
      label:'Hold For Other Mode'
    },
    {
      id: 'maintenanceOrderStatusName',
      label: 'Status',
      sortDisabled: true,
    },
    {
      id: 'maintenanceOrderStatusDuration',
      label: 'Status Duration',
      dataType: {
        type: 'string',
        formatter: (cellValue: unknown) => utils.formatDuration(cellValue as number),
      },
    },
    {
      id: 'assignee',
      label: 'Assign To',
    },
    {
      id: 'assigneeDate',
      label: 'Assigned Date',
      sortDisabled: true,
      dataType: {
        type: 'string',
        formatter: (value) => {
          const stringValue = value as string;
          const date = new Date(stringValue);
          return stringValue && date ? utils.dateFormatter.format(date) : '';
        },
      },
    },

    {
      id: 'maintenanceShopCode',
      label: 'Shop ID',
    },
    {
      id: 'locationCode',
      label: 'Location',
      sortDisabled: true,
    },
    {
      id: 'clusterName',
      label: 'Cluster',
    },
    {
      id: 'region',
      label: 'Region',
    },
    {
      id: 'shopCountry',
      label: 'Shop Country',
      sortDisabled: true,
    },
    {
      id: 'updateTimestamp',
      label: 'Last Updated On',
      sortDisabled: true,
      dataType: {
        type: 'string',
        formatter: (value) => {
          const stringValue = value as string;
          const date = new Date(stringValue);
          return stringValue && date ? utils.dateFormatter.format(date) : '';
        },
      },
    },
    {
      id: 'createTimestamp',
      label: 'Created On',
      sortDisabled: true,
      dataType: {
        type: 'string',
        formatter: (value) => {
          const date = new Date(value as string);
          return `${utils.dateFormatter.format(date)}`;
        },
      },
    },
    {
      id: 'createUserID',
      label: 'Created By',
      sortDisabled: true,
    },
  ];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(private approvalwoService: ApprovalWoService) {}

  ngOnInit() {
    this.sort = `createTimestamp:desc,updateTimestamp:desc`;
    this.urlNavigate = '/approvals/all';
    this.subscribeToFormChanges();
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.valueChanges
      .pipe(
        tap(() => {
          if (this.filterFormGroup.controls.workOrderFilterForm.controls['applyWOFilter']?.value) {
            this.getWorkOrdersApiCallStatus = 'IN_PROGRESS';
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        if (
          this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.value ||
          this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.value
        ) {
          this.onApplyFilter();
          this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(false);
          this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.setValue(false);
        }
      });
  }
  onSortDirectionChange(event: Event) {
    let customEvent = event as CustomEvent;
    let sortColumn: string = this.sortColumnName(customEvent.detail.column);
    let sortDirection: string = customEvent.detail.direction == 'ascending' ? 'asc' : 'desc';
    if (sortColumn != 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getWorkOrder();
    }
  }
  private sortColumnName(gridColumnName: string): string {
    switch (gridColumnName) {
      case 'maintenanceOrderStatusDuration':
        return 'maintenanceOrderStatusUpdatedTimestamp';
      case 'totalOrderCostUSD':
        return 'totalOrderCost';
      default:
        return gridColumnName;
    }
  }

  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;

    this.getWorkOrder();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getWorkOrder(true);
    }
  }

  onApplyFilter() {
    if (this.filterFormGroup.controls.workOrderFilterForm.valid) {
      const filters: manualApprovalFilter = {};

      if (this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.value) {
        filters.equipmentNumber = this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.value;
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value &&
        this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value.length > 0
      ) {
        filters.maintenanceRepairModes = this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value
          .map((x) => x.repairMode?.replace(/\-/g, ','))
          .join(',');
      }
      if (this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value) {
        filters.maintenanceOrderNumber = this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value;
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.status.value &&
        this.filterFormGroup.controls.workOrderFilterForm.controls.status.value.length > 0
      ) {
        filters.maintenanceOrderStatusTypes = this.filterFormGroup.controls.workOrderFilterForm.controls.status.value
          .map((x) => x.generatedCode?.replace(/\-/g, ','))
          .join(',');
      }
      if (this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.value) {
        filters.fromDate = this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.value;
      }
      if (this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.value) {
        filters.toDate = this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.value;
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.region.value &&
        this.filterFormGroup.controls.workOrderFilterForm.controls.region.value.length > 0
      ) {
        filters.region = this.filterFormGroup.controls.workOrderFilterForm.controls.region.value
          .map((x) => x.replace(/\-/g, ','))
          .join(',');
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.cluster.value &&
        this.filterFormGroup.controls.workOrderFilterForm.controls.cluster.value.length > 0
      ) {
        filters.cluster = this.filterFormGroup.controls.workOrderFilterForm.controls.cluster.value
          .map((x) => x.replace(/\-/g, ','))
          .join(',');
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.shopCountry.value &&
        this.filterFormGroup.controls.workOrderFilterForm.controls.shopCountry.value.length > 0
      ) {
        filters.shopCountry = this.filterFormGroup.controls.workOrderFilterForm.controls.shopCountry.value
          .map((x) => x.replace(/\-/g, ','))
          .join(',');
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value &&
        this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value.length > 0
      ) {
        filters.maintenanceShopCodes = this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value
          .map((x) => x.replace(/\-/g, ','))
          .join(',');
      }
      if (this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value) {
        filters.searchText = utils.normalizeSearchTextForApi(this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value);
      }
      if (this.filterFormGroup.controls.workOrderFilterForm.controls.assignedTo.value) {
        filters.assignee = this.filterFormGroup.controls.workOrderFilterForm.controls.assignedTo.value;
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.isOnHold.value === true ||
        this.filterFormGroup.controls.workOrderFilterForm.controls.isOnHold.value === false
      ) {
        filters.isOnHold = this.filterFormGroup.controls.workOrderFilterForm.controls.isOnHold.value;
      }
      this.filterParam = filters;
      this.currentPage = 1;
      this.getWorkOrder();
    }
  }

  getWorkOrder(isPageChange: boolean = false): void {
    this.getWorkOrdersApiCallStatus = 'IN_PROGRESS';
    this.filterParam.isProcessedByRuleEngine = true;
    this.filterParam.equipmentNumber = this.filterParam.equipmentNumber ? this.filterParam.equipmentNumber : [];
    this.filterParam.maintenanceOrderNumber = this.filterParam.maintenanceOrderNumber ? this.filterParam.maintenanceOrderNumber : [];
    this.filterParam.maintenanceOrderStatusTypes = this.filterParam.maintenanceOrderStatusTypes
      ? this.filterParam.maintenanceOrderStatusTypes
      : '';
    this.filterParam.assignee = this.filterParam.assignee ? this.filterParam.assignee : '';
    this.filterParam.cluster = this.filterParam.cluster ? this.filterParam.cluster : '';
    this.filterParam.maintenanceRepairModes = this.filterParam.maintenanceRepairModes ? this.filterParam.maintenanceRepairModes : '';
    this.filterParam.maintenanceShopCodes = this.filterParam.maintenanceShopCodes ? this.filterParam.maintenanceShopCodes : '';
    this.filterParam.maintenanceeShopCode = this.filterParam.maintenanceeShopCode ? this.filterParam.maintenanceeShopCode : '';
    this.filterParam.region = this.filterParam.region ? this.filterParam.region : '';
    this.filterParam.shopCountry = this.filterParam.shopCountry ? this.filterParam.shopCountry : '';
    this.filterParam.searchText = this.filterParam.searchText ? this.filterParam.searchText : '';
    this.filterParam.isOnHold = this.filterParam.isOnHold;

    this.approvalwoService
      .getApprovalWorkOrders(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          const body = response.body || [];
          this.data = body;
          this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
          this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
          this.currentPage = +headers.get('Current-Page')!;
          this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
          this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
          this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          this.apiVersion = headers.get('API-Version') ?? undefined;
          this.getWorkOrdersApiCallStatus = 'COMPLETED_SUCCESFULLY';

          if (this.data && this.data.length > 0) {
            this.approvalwoService.filterData = this.filterParam;
          }
        },
        error: (err) => {
          this.getWorkOrdersApiCallStatus = 'FAILED';
          this.data = [];
          this.approvalwoService.filterData = {};
          if (!isPageChange) {
            this.selectedRows = [];
            this.selectedRowsChange.emit(this.selectedRows);
            this.selected = ['']; // clear the selection on the grid
          }
        },
      });
  }

  clearFilter() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.setValue(true);
    this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
  }
}
